import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical } from 'lucide-react'

import { fr } from '@/i18n/fr'
import { cn } from '@/lib/utils'

interface Identified {
  id: string
}

interface Props<T extends Identified> {
  rows: T[]
  onReorder: (rows: T[]) => void
  children: (row: T, index: number, handle: React.ReactNode) => React.ReactNode
  className?: string
}

/**
 * Drag-to-reorder for both editors (NFR-19), shared so the two lists cannot drift apart —
 * the same reasoning as the ingredient editor's single component with two layouts (NFR-18).
 *
 * The handle is the whole point. Reordering was up/down buttons until the M3 review precisely
 * because a drag that activates anywhere on the row fights page scrolling on touch; passing
 * the listeners to one grip and nothing else is what answers that, and it is why the row
 * itself must never spread them.
 *
 * `KeyboardSensor` is not optional either: the buttons it replaces gave keyboard reordering
 * away for free, and dropping them without this would be a straight accessibility regression.
 */
export function SortableList<T extends Identified>({
  rows,
  onReorder,
  children,
  className,
}: Props<T>) {
  const sensors = useSensors(
    // A few pixels of slop before a drag starts, so a tap that lands on the handle and
    // wobbles is still a tap.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const from = rows.findIndex((row) => row.id === active.id)
    const to = rows.findIndex((row) => row.id === over.id)
    if (from === -1 || to === -1) return

    const next = [...rows]
    next.splice(to, 0, ...next.splice(from, 1))
    onReorder(next)
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={rows} strategy={verticalListSortingStrategy}>
        <div className={cn('flex flex-col gap-3', className)}>
          {rows.map((row, index) => (
            <SortableRow key={row.id} id={row.id}>
              {(handle) => children(row, index, handle)}
            </SortableRow>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  )
}

function SortableRow({
  id,
  children,
}: {
  id: string
  children: (handle: React.ReactNode) => React.ReactNode
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id })

  const handle = (
    <button
      type="button"
      ref={setActivatorNodeRef}
      // Only these listeners, and only here — see the component docstring.
      {...attributes}
      {...listeners}
      aria-label={fr.common.reorder}
      className="min-h-touch min-w-touch flex shrink-0 cursor-grab touch-none items-center justify-center rounded-lg text-stone-400 hover:bg-stone-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 active:cursor-grabbing dark:hover:bg-stone-800"
    >
      <GripVertical className="h-5 w-5" />
    </button>
  )

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(isDragging && 'relative z-10 opacity-80 shadow-lg')}
    >
      {children(handle)}
    </div>
  )
}
